import React, { useState, useEffect, useRef } from 'react';
import BubbleHeader from '../BubbleHeader';
import Tabs from '../Tabs';
import HomeTabContent from '../HomeTabContent';
import BubblePreviousConversations from '../BubblePreviousConversations';
import Chat from '../Chat';
import BubblePassword from '../BubblePassword';
import Badge from '../Badge';
import { useBubbleActions } from '@/hooks/useBubbleActions';
import ChatManager from '@/components/Chatbot/ChatManager';
import { useConversationStore } from '@/hooks/useConversationStore';
import EmailTranscriptWrapper from '../EmailTranscriptWrapper';
import FullDanteLogoIcon from '../Icons/FullDanteLogoIcon';
import FullDanteLogoGreyIcon from '../Icons/FullDanteLogoGreyIcon';

const Bubble = ({
  config,
  type, 
  slots, 
  accentColor = '#5745FA', // Default accent color, can be changed via backend config
  chatbotName,
  customIcon,
  onBackClick,
  onMenuClick,
  onCloseClick,
  children,
  chatData = null,
  onSendMessage,
  activeTab: externalActiveTab,
  onTabChange,
  className = '',
  kb_id,
  isPreviewMode = false,
  previous_conversation_enabled = config?.previous_conversation_enabled ?? true,
  isInApp = false,
  initialShouldFetchCustomization = false,
  showInAppHeader = false,
  conversationNamespace = null,
  ...props
}) => {
  // Initialize activeTab based on config and tab order
  const getInitialActiveTab = (clearStorage = false) => {
    const storedTab = localStorage.getItem('bubble-active-tab-after-reset');
    if (storedTab) {
      // Only clear the stored tab if explicitly requested
      if (clearStorage) {
        localStorage.removeItem('bubble-active-tab-after-reset');
      }
      return storedTab;
    }
    if (config?.initialActiveTab) {
      return config.initialActiveTab;
    }

    // If home tab is enabled, check the order to determine which tab should be first
    if (config?.home_tab_enabled && config?.main_tabs) {
      const homeOrder = config.main_tabs.home?.order || 1;
      const chatOrder = config.main_tabs.chat?.order || 2;

      // Return the tab with the lower order number (first tab)
      return homeOrder <= chatOrder ? 'home' : 'chat';
    }

    return config?.home_tab_enabled ? 'home' : 'chat';
  };

  // Check for reset state at component initialization
  const isFromReset = localStorage.getItem('bubble-active-tab-after-reset') === 'chat' && 
                      localStorage.getItem('bubble-active-view-after-reset') === 'chat';

  // Initialize currentView based on stored state or default logic
  const getInitialCurrentView = () => {
    if (isFromReset) {
      return 'chat'; // Force chat view when coming from reset
    }
    return 'home'; // Default view
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab());

  const [currentView, setCurrentView] = useState(getInitialCurrentView());
  const [showPasswordModal, setShowPasswordModal] = useState(!config?.public);
  const [cookie, setCookie] = useState(null); //this is used on bubble when password is enabled
  const [promptToSend, setPromptToSend] = useState(null);
  const [parentWindowWidth, setParentWindowWidth] = useState(window.innerWidth);
  const [initialMessagesCount, setInitialMessagesCount] = useState(0);
  const [hasSeenInitialMessages, setHasSeenInitialMessages] = useState(false);

  const conversationInterfaceType = conversationNamespace || (isInApp ? 'in-app' : 'bubble');

  // Use the conversation store with automatic namespacing
  const {
    currentConversation,
    resetCurrentConversation,
    setCurrentConversation
  } = useConversationStore(kb_id, conversationInterfaceType);
  const [isHumanHandover, setIsHumanHandover] = useState(false); // if true, then the header will be human handover header
  const [agentName, setAgentName] = useState('');
  const [agentTitle, setAgentTitle] = useState('');
  const [agentAvatar, setAgentAvatar] = useState(null);
  const [agentJoined, setAgentJoined] = useState(false);
  const [showEmailTranscript, setShowEmailTranscript] = useState(false);
  const [chatManagerKey, setChatManagerKey] = useState(0);

  // Ref to access ChatManager's handleMessagesOnChat function
  const chatManagerRef = useRef(null);
  const bubbleBodyRef = useRef(null);

  // Use centralized bubble actions hook
  const { resetChat, openPrivacyPolicy, openTermsOfService } = useBubbleActions(
    config,
    conversationInterfaceType
  );

  useEffect(() => {
    setShowPasswordModal(!config?.public);
  }, [config?.public]);

  // Track if we handled a reset to prevent other effects from overriding
  const hasHandledResetRef = useRef(false);

  // Handle reset state immediately on component mount
  useEffect(() => {
    const storedTab = localStorage.getItem('bubble-active-tab-after-reset');
    const storedView = localStorage.getItem('bubble-active-view-after-reset');
    
    if (storedTab === 'chat' && storedView === 'chat') {
      hasHandledResetRef.current = true; // Set immediately, synchronously
      setActiveTab('chat');
      setCurrentView('chat');
      // Clear the stored values after using them
      localStorage.removeItem('bubble-active-tab-after-reset');
      localStorage.removeItem('bubble-active-view-after-reset');
    }
  }, []); // Run only once on mount

  // Update active tab when config changes (but not on reset)
  useEffect(() => {    
    // Skip if we just handled a reset
    if (hasHandledResetRef.current) {
      return;
    }
    
    // Skip if we're still in the process of handling a reset
    const storedTab = localStorage.getItem('bubble-active-tab-after-reset');
    const storedView = localStorage.getItem('bubble-active-view-after-reset');
    
    if (storedTab === 'chat' && storedView === 'chat') {
      return;
    }
    
    // Normal logic when not from reset
    const newActiveTab = getInitialActiveTab(true); // Clear storage when called from useEffect
    setActiveTab(newActiveTab);

    // Update currentView based on the new active tab
    if (newActiveTab === 'home') {
      setCurrentView('home');
    } else if (newActiveTab === 'chat') {
      // For chat tab, show previous conversations by default, or chat if in app, preview mode, or previous conversations disabled
      const newView = (isInApp || isPreviewMode || !previous_conversation_enabled) ? 'chat' : 'previous-conversations';
      setCurrentView(newView);
    }
  }, [config?.home_tab_enabled, config?.initialActiveTab, config?.main_tabs, isInApp, isPreviewMode, previous_conversation_enabled]);
  // Get conversation store functions

  // Effect to count initial messages
  useEffect(() => {
    if (config?.initial_messages && Array.isArray(config.initial_messages)) {
      setInitialMessagesCount(config.initial_messages.length);
    }
  }, [config?.initial_messages]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    // Call external onTabChange if provided
    if (onTabChange) {
      onTabChange(tab);
    }

    // Reset to home view when switching tabs
    if (tab === 'home') {
      setCurrentView('home');
    }

    // When clicking chat tab, always show direct chat
    if (tab === 'chat') {
      setCurrentView('chat');
      setHasSeenInitialMessages(true);
    }

    // If switching to chat tab and there's a valid conversation ID,
    // ensure we're using the correct conversation type
    if (tab === 'chat' && config?.kb_id) {
      if (currentConversation?.id && currentConversation?.type === 'new') {
        // Update the conversation type to "opening" to ensure messages load
        setCurrentConversation({
          ...currentConversation,
          type: 'opening'
        });
      }
    }
  };

  const handleNavigate = (view) => {
    if (view === 'chat') {
      setActiveTab('chat');
      setCurrentView('chat'); // Explicitly set to chat view for new conversations
      setIsHumanHandover(false);
    } else if (view === 'chat-previous-conversations') {
      setActiveTab('chat');
      // If previous conversations are disabled, go directly to chat instead
      const targetView = previous_conversation_enabled ? 'previous-conversations' : 'chat';
      setCurrentView(targetView);
      setIsHumanHandover(false);
    } else {
      setCurrentView(view);
    }
  };

  const handleCloseClick = () => {
    const isInEmbed = window.parent !== window;

    if (isInEmbed) {
      try {
        const message = { eventType: 'chatbotCloseClick', eventData: true };
        window.parent.postMessage(message, '*');
      } catch (error) {
        // Silently fail if unable to send message to parent window
      }
    } else {
      if (onCloseClick) {
        onCloseClick();
      }
    }
  };

  const handleEmailTranscriptClick = () => {
    setShowEmailTranscript(true);
  };

  useEffect(() => {
    const message = { eventType: 'siteReady' };

    // Check if we're in a nested iframe (custom URL scenario)
    if (window.parent !== window.top) {
      // Send to top-level window for custom URLs
      window.top.postMessage(message, '*');
    } else {
      // Send to parent window for regular embeds
      window.parent.postMessage(message, '*');
    }

    // Also send a "bubbleReady" message to indicate this component is ready to receive messages
    const bubbleReadyMessage = { eventType: 'bubbleReady' };
    if (window.parent !== window.top) {
      window.top.postMessage(bubbleReadyMessage, '*');
    } else {
      window.parent.postMessage(bubbleReadyMessage, '*');
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.eventType === 'promptClick' || event.data.eventType === 'tooltipPromptClick') {
        // Reset conversation first, then set prompt after a small delay to avoid timing issues
        resetCurrentConversation();
        
        // Use setTimeout to ensure the conversation reset completes before setting the prompt
        setTimeout(() => {
          setPromptToSend(event.data.eventData);
        }, 50);
        
        setActiveTab('chat');
        setCurrentView('chat'); // Ensure we show the chat interface, not previous conversations
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
    // Request parent window width via postMessage
    useEffect(() => {
      const requestParentWidth = () => {
        try {
          // Try direct access first (same-origin case)
          setParentWindowWidth(window.parent.innerWidth);
        } catch (error) {
          // Cross-origin case: request width via postMessage
          window.parent.postMessage({ eventType: 'requestParentWidth' }, '*');
        }
      };
  
      requestParentWidth();
  
      // Listen for response from parent
      const handleMessage = (event) => {
        if (event.data.eventType === 'parentWidthResponse') {
          setParentWindowWidth(event.data.width);
        }
      };
  
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, []);

  return (
    <div className="bubble relative w-full h-full rounded-[28px] border border-white bg-gradient-to-br from-[var(--dt-color-surface-100)] from-[5.25%] to-[var(--dt-color-surface-100)] to-[57.52%] shadow-[0_10px_80px_0_rgba(5,1,45,0.10)] backdrop-blur-[50px] overflow-hidden flex flex-col">
        {/* Background blur effect */}
        <div
            className="absolute inset-0 rounded-full opacity-50 blur-[200px] shrink-0 z-0"
            style={{
              background: config?.brand_color,
              filter: 'blur(200px)',
              width: '317px',
              height: '317px'
            }}
        />
        {/* Header */}
        <BubbleHeader
          type={
            activeTab === 'home' && currentView === 'home'
              ? 'home'
              : currentView === 'previous-conversations' && previous_conversation_enabled
              ? 'messages'
              : isHumanHandover && agentJoined
              ? 'human-handover'
              : 'chat'
          }
          isPreviewMode={isPreviewMode}
          chatbotName={chatbotName || config?.name || config?.chatbot_name}
          customIcon={customIcon}
          conversationTitle="Messages"
          config={config}
          showPasswordModal={!config?.public && showPasswordModal}
          onBackClick={config?.home_tab_enabled || showEmailTranscript ? () => {
            if (showEmailTranscript) {
              setShowEmailTranscript(false);
              return;
            }

            if (activeTab === 'chat' && currentView === 'chat') {
              // From actual chat, go back to previous conversations (if enabled) or home
              if (previous_conversation_enabled) {
                setCurrentView('previous-conversations');
              } else {
                setActiveTab('home');
                setCurrentView('home');
              }
            } else if (activeTab === 'chat' && currentView === 'previous-conversations') {
              // From previous conversations (via chat tab), go to home
              setActiveTab('home');
              setCurrentView('home');
            } else if (currentView === 'previous-conversations') {
              // From previous conversations (via home tab), go to home
              setCurrentView('home');
            } else if (onBackClick) {
              // Default back behavior
              onBackClick();
            }
          } : null}
          onMenuClick={onMenuClick}
          onCloseClick={handleCloseClick}
          onResetChat={() => {
            resetChat(() => {
              setChatManagerKey((prev) => prev + 1);
              setActiveTab('chat');
              setCurrentView('chat');
              setPromptToSend(null);
              setShowEmailTranscript(false);
              setIsHumanHandover(false);
              setAgentName('');
              setAgentTitle('');
              setAgentAvatar(null);
              setAgentJoined(false);
              setHasSeenInitialMessages(false);
              localStorage.removeItem('bubble-active-tab-after-reset');
              localStorage.removeItem('bubble-active-view-after-reset');
              if (bubbleBodyRef.current) {
                bubbleBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
              }
            });
          }}
          onPrivacyPolicy={openPrivacyPolicy}
          onTermsOfService={openTermsOfService}
          onEmailTranscriptClick={handleEmailTranscriptClick}
          agentName={agentName}
          agentTitle={agentTitle}
          agentAvatar={agentAvatar}
          agentJoined={agentJoined}
        />
      
        {/* Content area */}
        <div ref={bubbleBodyRef} className={`bubble-body flex-1 overflow-visible min-h-0 z-10`}>
          {!(!config?.public && showPasswordModal) ? (
            <>
              {activeTab === 'home' && config?.home_tab_enabled ? (
                <div className="h-full overflow-y-auto no-scrollbar ">
                  {currentView === 'home' ? (
                    <HomeTabContent
                      onNavigate={handleNavigate}
                      kb_id={kb_id}
                      token={config?.token}
                      slots={slots}
                      config={config}
                      initialMessagesCount={initialMessagesCount}
                      hasSeenInitialMessages={hasSeenInitialMessages}
                      previous_conversation_enabled={previous_conversation_enabled}
                      isPreviewMode={isPreviewMode}
                    />
                  ) : currentView === 'previous-conversations' && previous_conversation_enabled ? (
                    <BubblePreviousConversations
                      kb_id={kb_id}
                      isPreviewMode={isPreviewMode}
                      previous_conversation_enabled={previous_conversation_enabled}
                      onConversationClick={() => {
                       // Handle conversation selection and switch to chat
                        setCurrentView('chat');
                      }}
                      onNavigate={handleNavigate}
                      config={config}
                      interfaceType={conversationInterfaceType}
                    />
                  ) : null}
                </div>
              ) : activeTab === 'chat' && currentView === 'previous-conversations' && previous_conversation_enabled ? (
                <div className="h-full overflow-y-auto no-scrollbar">
                  <BubblePreviousConversations
                    kb_id={kb_id}
                    isPreviewMode={isPreviewMode}
                    previous_conversation_enabled={previous_conversation_enabled}
                    onConversationClick={() => {
                     // Handle conversation selection and switch to actual chat
                      setCurrentView('chat');
                    }}
                    onNavigate={handleNavigate}
                    config={config}
                    interfaceType={conversationInterfaceType}
                  />
                </div>
              ) : (activeTab === 'chat' && currentView === 'chat') || !config?.home_tab_enabled ? (
                <ChatManager
                  ref={chatManagerRef}
                  key={chatManagerKey}
                  config={config}
                  isInApp={isInApp}
                  initialShouldFetchCustomization={initialShouldFetchCustomization}
                  isPreviewMode={isPreviewMode}
                  showInAppHeader={showInAppHeader}
                  promptToSend={promptToSend}
                  setPromptToSend={setPromptToSend}
                  onPromptProcessed={() => setPromptToSend(null)}
                  setIsHumanHandover={setIsHumanHandover}
                  setAgentName={setAgentName}
                  setAgentTitle={setAgentTitle}
                  setAgentAvatar={setAgentAvatar}
                  setAgentJoined={setAgentJoined}
                  // setShowEmailTranscript={setShowEmailTranscript}
                  // showEmailTranscript={showEmailTranscript}
                  // onNewConversationCreated={onNewConversationCreated}
                />
              ) : (
                children
              )}
            </>
          ) : null}
        </div>

        {/* Tabs */}
        {config?.home_tab_enabled && (activeTab !== 'chat' || (currentView === 'previous-conversations' && previous_conversation_enabled)) && !(!config?.public && showPasswordModal) && <Tabs
          homeTabName={config?.main_tabs?.home?.label || 'Home'}
          chatTabName={config?.main_tabs?.chat?.label || 'Chat'}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabOrder={{
            home: config?.main_tabs?.home?.order || 1,
            chat: config?.main_tabs?.chat?.order || 2
          }}
          config={config}
        />}

           {/* Password Overlay */}
        {!config?.public && showPasswordModal && (

              <BubblePassword
                kb_id={config?.kb_id}
                setCookie={setCookie}
                onUnlockSuccess={() => setShowPasswordModal(false)}
                onBubble={true}
                config={config}
              />

        )}

        {!config?.remove_watermark && !showEmailTranscript && <div className='bubble-footer px-v3-sm w-full'>
          <div className='flex items-center justify-center w-full py-[12px]'>
            <div className="bubble-powered-by flex items-center gap-1 cursor-pointer" onClick={() => {
              window.open(`https://dante-ai.com/?utm_source=cht-${kb_id}&utm_medium=pwdByDante&utm_campaign=poweredByDante`, '_blank');
            }}>
              <p className="text-xs font-medium text-v3-black-secondary leading-4">Powered by </p>
              <FullDanteLogoGreyIcon className="w-[56px] h-[14px]" />
            </div>
          </div>
        </div>}

     

        {/* Email Transcript Modal */}
        {showEmailTranscript && (
            <EmailTranscriptWrapper
              token={config?.token}
              conversationId={currentConversation?.id}
              onCancel={() => setShowEmailTranscript(false)}
              onSuccess={() => setShowEmailTranscript(false)}
              handleMessagesOnChat={chatManagerRef.current?.handleMessagesOnChat}
            />
        )}
    </div>
  );
};

export default Bubble;
